<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import MemberHeader from "@/components/member/MemberHeader.vue";
import MemberSidebar from "@/components/member/MemberSidebar.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const router = useRouter();

const stats = ref({
  projectsValidated: 28,
  activeValidations: 5,
  creditsVerified: "12,450",
  averageTime: "14 days",
});

const pendingValidations = ref([
  {
    id: "VAL-001",
    projectName: "Biomass Energy Project",
    developer: "Green Energy Ltd",
    submitted: "2024-01-10",
    deadline: "2024-01-25",
    priority: "High",
  },
  {
    id: "VAL-002",
    projectName: "Forest Conservation Initiative",
    developer: "EcoForest Corp",
    submitted: "2024-01-12",
    deadline: "2024-01-27",
    priority: "Medium",
  },
  {
    id: "VAL-003",
    projectName: "Methane Capture System",
    developer: "Clean Tech Solutions",
    submitted: "2024-01-14",
    deadline: "2024-01-29",
    priority: "Low",
  },
]);
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    
    <nav id="sidebar" class="w-64 bg-white border-r border-gray-200 min-h-screen">
        <div class="p-5">
            <div class="space-y-2">
                <span class="flex items-center space-x-3 px-3 py-2 rounded-lg bg-primary/10 text-primary cursor-pointer">
                    <i class="fas fa-tachometer-alt text-sm"></i>
                    <span class="text-sm font-medium">{{ $t('auditor.sidebar.dashboard') }}</span>
                </span>
                <span class="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 cursor-pointer">
                    <i class="fas fa-clipboard-check text-sm"></i>
                    <span class="text-sm font-medium">{{ $t('auditor.sidebar.assigned_projects') }}</span>
                </span>
                <span class="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 cursor-pointer">
                    <i class="fas fa-file-alt text-sm"></i>
                    <span class="text-sm font-medium">{{ $t('auditor.sidebar.verification_reports') }}</span>
                </span>
                <span class="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 cursor-pointer">
                    <i class="fas fa-calendar-alt text-sm"></i>
                    <span class="text-sm font-medium">{{ $t('auditor.sidebar.validation_schedule') }}</span>
                </span>
                <span class="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 cursor-pointer">
                    <i class="fas fa-cog text-sm"></i>
                    <span class="text-sm font-medium">{{ $t('auditor.sidebar.settings') }}</span>
                </span>
            </div>
        </div>
    </nav>
    
    <main id="main-content" class="flex-1 p-6">
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-accent mb-2">{{ $t('auditor.dashboard_page.title') }}</h1>
        <p class="text-gray-600">
          {{ $t('auditor.dashboard_page.subtitle') }}
        </p>
      </div>

      <div
        id="stats-overview"
        class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
      >
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600 mb-1">{{ $t('auditor.dashboard_page.stats.assigned_projects') }}</p>
              <p class="text-2xl font-bold text-accent">12</p>
            </div>
            <div
              class="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-project-diagram text-primary"></i>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600 mb-1">{{ $t('auditor.dashboard_page.stats.pending_reviews') }}</p>
              <p class="text-2xl font-bold text-secondary">7</p>
            </div>
            <div
              class="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-clock text-secondary"></i>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600 mb-1">{{ $t('auditor.dashboard_page.stats.completed_reports') }}</p>
              <p class="text-2xl font-bold text-accent">34</p>
            </div>
            <div
              class="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-check-circle text-accent"></i>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600 mb-1">{{ $t('auditor.dashboard_page.stats.validation_score') }}</p>
              <p class="text-2xl font-bold text-primary">98%</p>
            </div>
            <div
              class="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-star text-primary"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div
          id="assigned-projects"
          class="bg-white rounded-lg border border-gray-200"
        >
          <div class="p-6 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <h2 class="text-lg font-semibold text-accent">
                {{ $t('auditor.dashboard_page.assigned_projects.title') }}
              </h2>
              <button class="text-primary hover:text-primary/80 text-sm">
                {{ $t('auditor.dashboard_page.assigned_projects.view_all') }}
              </button>
            </div>
          </div>
          <div class="p-6">
            <div class="space-y-4">
              <div
                class="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div class="flex-1">
                  <h3 class="font-medium text-accent mb-1">
                    Solar Farm Expansion - Kenya
                  </h3>
                  <p class="text-sm text-gray-600 mb-2">
                    Category A • 250,000 tCO2e
                  </p>
                  <div class="flex items-center space-x-2">
                    <span
                      class="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full"
                      >{{ $t('auditor.dashboard_page.assigned_projects.validation_due') }}</span
                    >
                    <span class="text-xs text-gray-500">{{ $t('auditor.dashboard_page.assigned_projects.due') }}: Dec 25, 2024</span>
                  </div>
                </div>
                <button class="text-primary hover:text-primary/80">
                  <i class="fas fa-arrow-right"></i>
                </button>
              </div>

              <div
                class="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div class="flex-1">
                  <h3 class="font-medium text-accent mb-1">
                    Wind Energy Project - Brazil
                  </h3>
                  <p class="text-sm text-gray-600 mb-2">
                    Category B • 180,000 tCO2e
                  </p>
                  <div class="flex items-center space-x-2">
                    <span
                      class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                      >{{ $t('auditor.dashboard_page.assigned_projects.in_progress') }}</span
                    >
                    <span class="text-xs text-gray-500"
                      >{{ $t('auditor.dashboard_page.assigned_projects.started') }}: Dec 10, 2024</span
                    >
                  </div>
                </div>
                <button class="text-primary hover:text-primary/80">
                  <i class="fas fa-arrow-right"></i>
                </button>
              </div>

              <div
                class="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div class="flex-1">
                  <h3 class="font-medium text-accent mb-1">
                    Reforestation Initiative - Colombia
                  </h3>
                  <p class="text-sm text-gray-600 mb-2">
                    Category C • 95,000 tCO2e
                  </p>
                  <div class="flex items-center space-x-2">
                    <span
                      class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                      >{{ $t('auditor.dashboard_page.assigned_projects.completed') }}</span
                    >
                    <span class="text-xs text-gray-500"
                      >{{ $t('auditor.dashboard_page.assigned_projects.completed') }}: Dec 5, 2024</span
                    >
                  </div>
                </div>
                <button class="text-primary hover:text-primary/80">
                  <i class="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          id="validation-status"
          class="bg-white rounded-lg border border-gray-200"
        >
          <div class="p-6 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-accent">{{ $t('auditor.dashboard_page.validation_status.title') }}</h2>
          </div>
          <div class="p-6">
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-600">{{ $t('auditor.dashboard_page.validation_status.projects_validated') }}</span>
                <span class="text-sm font-medium text-accent">34/41</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-primary h-2 rounded-full"
                  style="width: 83%"
                ></div>
              </div>

              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-600">{{ $t('auditor.dashboard_page.validation_status.reports_submitted') }}</span>
                <span class="text-sm font-medium text-accent">28/34</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-secondary h-2 rounded-full"
                  style="width: 82%"
                ></div>
              </div>

              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-600">{{ $t('auditor.dashboard_page.validation_status.compliance_rate') }}</span>
                <span class="text-sm font-medium text-accent">98%</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-accent h-2 rounded-full"
                  style="width: 98%"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        id="recent-reports"
        class="bg-white rounded-lg border border-gray-200 mb-8"
      >
        <div class="p-6 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-accent">
              {{ $t('auditor.dashboard_page.recent_reports.title') }}
            </h2>
            <button
              class="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90"
            >
              <i class="fas fa-plus mr-2"></i>{{ $t('auditor.dashboard_page.recent_reports.upload_report') }}
            </button>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {{ $t('auditor.dashboard_page.recent_reports.project') }}
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {{ $t('auditor.dashboard_page.recent_reports.type') }}
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {{ $t('auditor.dashboard_page.recent_reports.status') }}
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {{ $t('auditor.dashboard_page.recent_reports.date') }}
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {{ $t('auditor.dashboard_page.recent_reports.actions') }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-accent">
                    Solar Farm Expansion - Kenya
                  </div>
                  <div class="text-sm text-gray-500">SOR-2024-001</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ $t('auditor.dashboard_page.recent_reports.validation_report') }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                    >{{ $t('auditor.dashboard_page.recent_reports.approved') }}</span
                  >
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  Dec 15, 2024
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <button class="text-primary hover:text-primary/80 mr-3">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button class="text-gray-600 hover:text-gray-800">
                    <i class="fas fa-download"></i>
                  </button>
                </td>
              </tr>
              <tr>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-accent">
                    Wind Energy Project - Brazil
                  </div>
                  <div class="text-sm text-gray-500">SOR-2024-002</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ $t('auditor.dashboard_page.recent_reports.verification_report') }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full"
                    >{{ $t('auditor.dashboard_page.recent_reports.under_review') }}</span
                  >
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  Dec 12, 2024
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <button class="text-primary hover:text-primary/80 mr-3">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button class="text-gray-600 hover:text-gray-800">
                    <i class="fas fa-edit"></i>
                  </button>
                </td>
              </tr>
              <tr>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-accent">
                    Reforestation Initiative - Colombia
                  </div>
                  <div class="text-sm text-gray-500">SOR-2024-003</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ $t('auditor.dashboard_page.recent_reports.validation_report') }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                    >{{ $t('auditor.dashboard_page.recent_reports.approved') }}</span
                  >
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  Dec 8, 2024
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <button class="text-primary hover:text-primary/80 mr-3">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button class="text-gray-600 hover:text-gray-800">
                    <i class="fas fa-download"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        id="quick-actions"
        class="bg-white rounded-lg border border-gray-200"
      >
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-accent">{{ $t('auditor.dashboard_page.quick_actions.title') }}</h2>
        </div>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              class="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div
                class="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-upload text-primary"></i>
              </div>
              <div class="text-left">
                <p class="font-medium text-accent">{{ $t('auditor.dashboard_page.quick_actions.upload_report.title') }}</p>
                <p class="text-sm text-gray-600">{{ $t('auditor.dashboard_page.quick_actions.upload_report.description') }}</p>
              </div>
            </button>

            <button
              class="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div
                class="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-calendar-plus text-secondary"></i>
              </div>
              <div class="text-left">
                <p class="font-medium text-accent">{{ $t('auditor.dashboard_page.quick_actions.schedule_validation.title') }}</p>
                <p class="text-sm text-gray-600">{{ $t('auditor.dashboard_page.quick_actions.schedule_validation.description') }}</p>
              </div>
            </button>

            <button
              class="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div
                class="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-file-signature text-accent"></i>
              </div>
              <div class="text-left">
                <p class="font-medium text-accent">{{ $t('auditor.dashboard_page.quick_actions.update_declaration.title') }}</p>
                <p class="text-sm text-gray-600">
                  {{ $t('auditor.dashboard_page.quick_actions.update_declaration.description') }}
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
